import {
  HealingRoom,
  PaginationData,
} from 'constants/interface';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

export interface CreateHealingRoomData extends Omit<
  HealingRoom,
  'id' |
  'createdAt' |
  'updatedAt' |
  'createdBy' |
  'updatedBy'
> {}

export interface UpdateHealingRoomData {
  id: string
  data: CreateHealingRoomData
}

export interface HealingRoomApi {
  createHealingRoom(healingRoomData: CreateHealingRoomData): Promise<HealingRoom>
  updateHealingRoom(healingRoomData: UpdateHealingRoomData): Promise<HealingRoom>
  getHealingRoom(healingRoomId: string): Promise<HealingRoom>
  getHealingRoomList(filter: PaginationData): Promise<PaginatedResponse<HealingRoom>>
}

const healingRoomApiBuilder = (): HealingRoomApi => {
  const createHealingRoom = async (
    healingRoomData: CreateHealingRoomData,
  ): Promise<HealingRoom> => {
    const params: Record<string, unknown> = {
      ...healingRoomData,
    };
    const result = await requestClient.post('/healing-rooms', params);

    const createdHealingRoom: HealingRoom = result.data as HealingRoom;
    return createdHealingRoom;
  };

  const updateHealingRoom = async (
    healingRoomData: UpdateHealingRoomData,
  ): Promise<HealingRoom> => {
    const { id: healingRoomId, data } = healingRoomData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/healing-rooms/${healingRoomId}`, params);
    const updatedHealingRoom: HealingRoom = result.data as HealingRoom;
    return updatedHealingRoom;
  };

  const getHealingRoom = async (
    healingRoomId: string,
  ): Promise<HealingRoom> => {
    const result = await requestClient.get(`/healing-rooms/${healingRoomId}`);
    const healingRoom: HealingRoom = result.data as HealingRoom;
    return healingRoom;
  };

  const getHealingRoomList = async (
    filterData: PaginationData,
  ): Promise<PaginatedResponse<HealingRoom>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/healing-rooms', params);
    const pagination = getPaginationHeaders(result);
    const healingRoomList: HealingRoom[] = result.data as HealingRoom[];
    return {
      pagination,
      data: healingRoomList,
    };
  };

  return {
    createHealingRoom,
    updateHealingRoom,
    getHealingRoom,
    getHealingRoomList,
  };
};

export const healingRoomApi = healingRoomApiBuilder();
